import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';

const PROGRAMS = [
  {
    icon: 'fa-baby',
    title: 'Early Years',
    subtitle: 'Play Group, Nursery, KG',
    color: 'from-pink-500 to-pink-600',
    bg: 'bg-pink-50',
    features: ['Montessori-based learning', 'Nazra and Fehm-ul-Quran', 'Creative play activities', 'Social development skills'],
  },
  {
    icon: 'fa-backpack',
    title: 'Primary Education',
    subtitle: 'Grade 1 – Grade 5',
    color: 'from-primary to-primary-dark',
    bg: 'bg-blue-50',
    features: ['Core subjects foundation', 'Islamic studies', 'Computer literacy basics', 'Extracurricular activities'],
  },
  {
    icon: 'fa-graduation-cap',
    title: 'Middle School',
    subtitle: 'Grade 6 – Grade 8',
    color: 'from-green-500 to-green-600',
    bg: 'bg-green-50',
    features: ['Advanced curriculum', 'Science & mathematics', 'Language development', 'Leadership skills'],
  },
];

const FACILITIES = [
  { icon: 'fa-laptop', title: 'Computer Lab', desc: 'Well-equipped with modern technology for digital learning and programming.', color: 'bg-primary/10 text-primary' },
  { icon: 'fa-book', title: 'Library', desc: 'Well-stocked with extensive collection of books, references, and digital resources.', color: 'bg-accent/10 text-accent' },
  { icon: 'fa-flask', title: 'Science Lab', desc: 'Modern laboratory for hands-on experiments and scientific exploration.', color: 'bg-green-100 text-green-600' },
  { icon: 'fa-running', title: 'Playground', desc: 'Vast playgrounds for sports, physical activities, and outdoor recreation.', color: 'bg-amber-100 text-amber-600' },
];

const STATIC_BOOKLISTS = [
  { grade: 'Nursery', badge: 'Early Years', badgeColor: 'bg-pink-500', file: 'NURSERY-BOOKLIST-2025-26.pdf' },
  { grade: 'KG', badge: 'Early Years', badgeColor: 'bg-pink-500', file: 'KG-BOOKLIST-2025-26.pdf' },
  { grade: 'Grade 1', badge: 'Primary', badgeColor: 'bg-primary', file: 'GRADE-1-BOOKLIST-202526.pdf' },
  { grade: 'Grade 2', badge: 'Primary', badgeColor: 'bg-primary', file: 'GRADE-2-BOOKLIST-2025-26.pdf' },
  { grade: 'Grade 3', badge: 'Primary', badgeColor: 'bg-primary', file: 'GRADE-3-BOOKLIST-2025-26.pdf' },
  { grade: 'Grade 4', badge: 'Primary', badgeColor: 'bg-primary', file: 'GRADE-4-BOOKLIST-2025-26.pdf' },
  { grade: 'Grade 5', badge: 'Primary', badgeColor: 'bg-primary', file: 'GRADE-5-BOOKLIST-2025-26.pdf' },
  { grade: 'Grade 6', badge: 'Middle', badgeColor: 'bg-green-600', file: 'GRADE-6-BOOKLIST-2025-26.pdf' },
  { grade: 'Grade 7', badge: 'Middle', badgeColor: 'bg-green-600', file: 'GRADE-7-BOOKLIST-2025-26.pdf' },
  { grade: 'Grade 8', badge: 'Middle', badgeColor: 'bg-green-600', file: 'GRADE-8-BOOKLIST-2025-26.pdf' },
];

const Academics = () => {
  const [booklists, setBooklists] = useState(STATIC_BOOKLISTS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch_ = async () => {
      try {
        const res = await fetch('./admin/api/booklist.php');
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.data?.length) setBooklists(data.data);
        }
      } catch { /* use static */ } finally {
        setLoading(false);
      }
    };
    fetch_();
  }, []);

  return (
    <div className="page-enter">
      <Helmet>
        <title>Academics — PAK School &amp; College System | Programs &amp; Curriculum</title>
        <meta name="description" content="Explore PAK School & College System's comprehensive academic programs, curriculum, booklists, and educational resources. From nursery to Grade 8." />
        <meta name="keywords" content="PAK School Academics, School Curriculum, Booklists, Academic Programs, Educational Resources, Mardan School" />
        <link rel="canonical" href="https://pakschoolandcollege.com/academics" />
      </Helmet>

      {/* Hero */}
      <div className="page-hero">
        <div className="page-hero-content">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white/80 text-sm mb-5">
            📚 Academic Excellence
          </div>
          <h1><span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">Academics</span></h1>
          <p>Excellence in education through comprehensive curriculum and modern teaching methods</p>
        </div>
      </div>

      {/* Programs */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="section-badge mx-auto w-fit">Our Programs</div>
            <h2 className="section-title">Academic Programs</h2>
            <p className="section-subtitle mx-auto">Comprehensive education tailored for every stage of development</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {PROGRAMS.map((prog, i) => (
              <div key={i} className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 overflow-hidden">
                <div className={`h-2 bg-gradient-to-r ${prog.color}`} />
                <div className="p-7">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${prog.color} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}>
                    <i className={`fas ${prog.icon} text-white text-2xl`} />
                  </div>
                  <h3 className="font-poppins font-800 text-navy text-xl mb-1">{prog.title}</h3>
                  <p className="text-gray-400 font-inter text-sm mb-5">{prog.subtitle}</p>
                  <ul className="space-y-2.5">
                    {prog.features.map((f, fi) => (
                      <li key={fi} className="flex items-center gap-3 text-sm text-gray-600 font-inter">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booklist */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="section-badge mx-auto w-fit">Resources</div>
            <h2 className="section-title">Booklist &amp; Resources</h2>
            <p className="section-subtitle mx-auto">Download required books and materials for each grade level</p>
          </div>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {booklists.map((bl, i) => (
                <div key={i} className="group bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <i className="fas fa-book text-primary" />
                    </div>
                    <span className={`${bl.badgeColor} text-white text-xs font-poppins font-700 px-2 py-1 rounded-full`}>
                      {bl.badge}
                    </span>
                  </div>
                  <h3 className="font-poppins font-700 text-navy text-sm mb-1">{bl.grade}</h3>
                  <p className="text-gray-400 text-xs font-inter mb-3">Academic Year 2025–26</p>
                  <a
                    href={bl.file_path ? `./admin/assets/booklist/${bl.file_path}` : `./admin/assets/booklist/${bl.file}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-1.5 w-full py-2 rounded-xl bg-primary text-white text-xs font-poppins font-600 hover:bg-primary-dark transition-colors group-hover:shadow-md"
                    onClick={(e) => { if (!bl.file && !bl.file_path) e.preventDefault(); }}
                  >
                    <i className="fas fa-download text-[10px]" />
                    View PDF
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Facilities */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="section-badge mx-auto w-fit">Our Facilities</div>
            <h2 className="section-title">World-Class Facilities</h2>
            <p className="section-subtitle mx-auto">Modern infrastructure designed to support quality education and holistic development</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FACILITIES.map((fac, i) => (
              <div key={i} className="group bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 text-center">
                <div className={`w-14 h-14 rounded-2xl ${fac.color} flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`fas ${fac.icon} text-xl`} />
                </div>
                <h3 className="font-poppins font-700 text-navy text-base mb-2">{fac.title}</h3>
                <p className="text-gray-500 font-inter text-sm leading-relaxed">{fac.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-primary/5 to-accent/5">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="font-poppins font-800 text-navy text-2xl sm:text-3xl mb-4">Want to Learn More?</h2>
          <p className="text-gray-500 font-inter mb-7">Contact our academic team for detailed information about programs, curriculum, and enrollment.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="tel:03430001144" className="btn-primary inline-flex">
              <i className="fas fa-phone" /> Call Us Now
            </a>
            <a href="/contact" className="btn-outline inline-flex">
              <i className="fas fa-envelope" /> Contact Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Academics;
